import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_BASE_URL } from '../../../config/config';

dayjs.extend(duration);
dayjs.extend(relativeTime);

// Custom status indicator
const StatusDot = ({ status }) => (
  <Box
    sx={{
      display: 'inline-block',
      width: 14,
      height: 14,
      borderRadius: '50%',
      backgroundColor: status === 'ON' ? '#f44336' : status === 'PLEASE_WAIT' ? '#ff9800' : '#4caf50',
      boxShadow: '0 0 6px 2px rgba(0,0,0,0.10)',
      mr: 1,
      border: '2px solid #fff',
    }}
  />
);

// ----------------- MAIN COMPONENT -----------------
const MotorStatusBox = ({ motorData, userPermission, onStatusChange, deviceCode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [motorStatus, setMotorStatus] = useState(motorData?.motorStatus || 'OFF');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  // Format status string
  const formatMotorStatus = () => {
    if (motorStatus === 'PLEASE_WAIT') return 'Waiting for response...';
    const { offOnSince, forced } = motorData || {};
    const isManual = forced === 'MANUAL_ON' || forced === 'MANUAL_OFF';
    const manualText = isManual ? (forced === 'MANUAL_ON' ? 'Manual ON' : 'Manual OFF') : '';
    if (!offOnSince || offOnSince.totalMinutes <= 0) {
      return isManual ? `${manualText} (Just Now)` : `${motorStatus} (Just Now)`;
    }
    const now = dayjs();
    const dateSince = now.subtract(offOnSince.totalMinutes, 'minute');
    const formattedTime = dateSince.format('h:mm A, D MMM');
    const forString = `${offOnSince.hours}h ${offOnSince.minutes}m`;
    return isManual
      ? `${manualText} at ${formattedTime}, for ${forString}`
      : `${motorStatus} since ${formattedTime}, for ${forString}`;
  };

  const lastUpdateTimestamp = motorStatus === 'ON' ? motorData?.motorOnTimestamp : motorData?.motorOffTimestamp;
  const relativeLastUpdate = lastUpdateTimestamp ? dayjs(lastUpdateTimestamp).fromNow() : 'Just Now';

  // Toggle API
  const performToggle = async (desiredState) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/sensor/toggle-motor/${deviceCode}`,
        { state: desiredState }
      );
      const updatedData = response.data;
      setMotorStatus(updatedData.motorStatus || 'OFF');
      setSnackbar({ open: true, message: updatedData.message, severity: 'info' });
      if (onStatusChange) onStatusChange(updatedData);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to toggle motor state.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Toggle handler
  const handleToggle = () => {
    if (userPermission !== 'edit') {
      setSnackbar({ open: true, message: 'No permission to toggle motor.', severity: 'error' });
      return;
    }
    const desiredState = motorStatus === 'ON' ? 'OFF' : 'ON';
    if (desiredState === 'ON') setOpenConfirmationDialog(true);
    else performToggle(desiredState);
  };
  const handleConfirmTurnOn = () => { setOpenConfirmationDialog(false); performToggle('ON'); };
  const handleCancelTurnOn = () => { setOpenConfirmationDialog(false); };

  // Only show manual indicator for manual states
  const isManual = motorData?.forced === 'MANUAL_ON' || motorData?.forced === 'MANUAL_OFF';

  return (
    <>
      <Card
        sx={{
          maxWidth: 700,
          mx: 'auto',
          mt: 2,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(44, 62, 80, 0.10)',
          background: motorStatus === 'ON' ? 'linear-gradient(90deg, #ffe5e5 0%, #fff 100%)' : 'linear-gradient(90deg, #e5ffe5 0%, #fff 100%)',
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            {/* Status dot and label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StatusDot status={motorStatus} />
              <PowerSettingsNewIcon sx={{ color: motorStatus === 'ON' ? '#f44336' : '#4caf50', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700} color={motorStatus === 'ON' ? '#f44336' : '#4caf50'}>
                Motor {motorStatus}
              </Typography>
              {isManual && (
                <Box sx={{ ml: 1, px: 1, py: 0.5, bgcolor: '#ffecb3', borderRadius: 1, fontSize: 12, color: '#ff9800', fontWeight: 600 }}>
                  Manual
                </Box>
              )}
            </Box>
            {/* Status info */}
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, textAlign: 'center' }}>
              {formatMotorStatus()}
            </Typography>
            {/* Last update */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: '#888' }} />
              <Typography variant="caption" color="text.secondary">
                {relativeLastUpdate}
              </Typography>
            </Box>
            {/* Toggle button */}
            <IconButton
              onClick={handleToggle}
              disabled={loading || motorStatus === 'PLEASE_WAIT' || userPermission !== 'edit'}
              sx={{
                bgcolor: motorStatus === 'ON' ? '#f44336' : '#4caf50',
                color: '#fff',
                '&:hover': { bgcolor: motorStatus === 'ON' ? '#d32f2f' : '#388e3c' },
                boxShadow: '0 2px 8px rgba(44,62,80,0.12)',
              }}
              size="large"
              aria-label="toggle motor"
            >
              <PowerSettingsNewIcon />
            </IconButton>
          </Box>
          {loading && (
            <Typography variant="caption" sx={{ color: 'gray', fontSize: '12px', mt: 1 }}>
              Updating...
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog when Turning Motor ON */}
      <Dialog open={openConfirmationDialog} onClose={handleCancelTurnOn}>
        <DialogTitle>Confirm Motor Activation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to turn the motor ON? This action will activate the motor.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTurnOn} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmTurnOn} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', fontSize: { xs: '12px', sm: 'inherit' } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MotorStatusBox;
